'use client';
// 修改自 https://21st.dev/preetsuthar17/tree-view/default

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, File } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { BaseFillFolder } from '@/app/components/icons/BaseFillFolder';
import { cn } from '@/app/utils';

export enum TreeNodeType {
  FOLDER = 'folder',
  FILE = 'file',
}

// Types
export type TreeNode = {
  id: string;
  label: string | React.ReactNode;
  type: TreeNodeType;
  icon?: React.ReactNode;
  children?: TreeNode[];
  expandable?: boolean;
};

export type TreeViewProps = {
  data: TreeNode[];
  onNodeClick?: (node: TreeNode) => void;
  onNodeExpand?: (nodeId: string, expanded: boolean) => void;
  defaultExpandedIds?: string[];
  showLines?: boolean;
  showIcons?: boolean;
  selectable?: boolean;
  multiSelect?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  indent?: number;
  animateExpand?: boolean;
};

export function TreeView({
  data,
  onNodeClick,
  onNodeExpand,
  defaultExpandedIds = [],
  showLines = true,
  showIcons = true,
  selectable = true,
  multiSelect = false,
  selectedIds = [],
  onSelectionChange,
  indent = 20,
  animateExpand = true,
}: TreeViewProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(defaultExpandedIds));
  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>(selectedIds);

  const isControlled = selectedIds !== undefined && onSelectionChange !== undefined;
  const currentSelectedIds = isControlled ? selectedIds : internalSelectedIds;

  const toggleExpanded = (nodeId: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      const isExpanded = newSet.has(nodeId);
      isExpanded ? newSet.delete(nodeId) : newSet.add(nodeId);
      onNodeExpand?.(nodeId, !isExpanded);
      return newSet;
    });
  };

  const handleSelection = (nodeId: string, ctrlKey = false) => {
    if (!selectable) return;

    let newSelection: string[];

    if (multiSelect && ctrlKey) {
      newSelection = currentSelectedIds.includes(nodeId)
        ? currentSelectedIds.filter((id) => id !== nodeId)
        : [...currentSelectedIds, nodeId];
    } else {
      newSelection = currentSelectedIds.includes(nodeId) ? [] : [nodeId];
    }

    isControlled ? onSelectionChange?.(newSelection) : setInternalSelectedIds(newSelection);
  };

  const renderNode = (node: TreeNode, level = 0, isLast = false, parentPath: boolean[] = []) => {
    const isFolder = node.type === TreeNodeType.FOLDER;
    const hasChildren = (node.children?.length ?? 0) > 0;
    const isExpanded = expandedIds.has(node.id);
    const isSelected = currentSelectedIds.includes(node.id);
    const currentPath = [...parentPath, isLast];

    const getDefaultIcon = () => (isFolder ? <BaseFillFolder className="text-2xl" /> : <File className="size-6" />);

    return (
      <div key={node.id} className="select-none">
        <motion.div
          className={cn(
            'group relative mx-2 mb-2 flex h-11 cursor-pointer items-center rounded-md px-3 transition-all duration-200',
            'hover:bg-[#2C323D]',
            isSelected && 'bg-[#2C323D]',
          )}
          style={{ paddingLeft: level * indent + 8 }}
          onClick={(e) => {
            if (isFolder && node.expandable) toggleExpanded(node.id);
            handleSelection(node.id, e.ctrlKey || e.metaKey);
            onNodeClick?.(node);
          }}
          whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
        >
          {/* Tree Lines */}
          {showLines && level > 0 && (
            <div className="-left-3 pointer-events-none absolute top-0 bottom-0">
              {parentPath.map(
                (isLastInPath, pathIndex) =>
                  !isLastInPath && (
                    <div
                      key={`line-v-${node.id}-${pathIndex}`}
                      className="absolute top-0 bottom-0 w-px bg-system-white10"
                      style={{
                        left: pathIndex * indent + 24,
                      }}
                    />
                  ),
              )}
              <div
                className="absolute top-1/2 h-px w-3.5 bg-system-white10"
                style={{
                  left: (level - 1) * indent + 24,
                }}
              />
              {!isLast && (
                <div
                  className="absolute top-0 bottom-0 w-px bg-system-white10"
                  style={{
                    left: (level - 1) * indent + 24,
                  }}
                />
              )}
            </div>
          )}

          {/* Expand Icon */}
          <motion.div
            className="mr-1 flex h-4 w-4 items-center justify-center"
            animate={{ rotate: isFolder && isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            {isFolder && node.expandable && <ChevronRight className="h-3 w-3 text-system-white" />}
          </motion.div>

          {/* Node Icon */}
          {showIcons && (
            <motion.div
              className="mr-2 flex size-6 items-center justify-center text-system-white"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.15 }}
            >
              {node.icon || getDefaultIcon()}
            </motion.div>
          )}

          {/* Label */}
          <span className="font flex-1 truncate text-sm">{node.label}</span>
        </motion.div>

        {/* Children */}
        <AnimatePresence>
          {isFolder && isExpanded && hasChildren && node.expandable && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                duration: animateExpand ? 0.3 : 0,
                ease: 'easeInOut',
              }}
              className="overflow-hidden"
            >
              <motion.div
                initial={{ y: -10 }}
                animate={{ y: 0 }}
                exit={{ y: -10 }}
                transition={{
                  duration: animateExpand ? 0.2 : 0,
                  delay: animateExpand ? 0.1 : 0,
                }}
              >
                {node.children?.map((child, index) => (
                  <div key={child.id}>
                    {renderNode(child, level + 1, index === (node.children?.length ?? 0) - 1, currentPath)}
                  </div>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <>
      {data.map((node, index) => (
        <div key={node.id}>{renderNode(node, 0, index === data.length - 1)}</div>
      ))}
    </>
  );
}
